import { cn } from '../../utils/helpers';
import { createContext, useContext, useState } from 'react';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const TabsContext = createContext<TabsContextValue>({
  activeTab: '',
  setActiveTab: () => {},
});

interface TabsProps {
  defaultValue: string;
  activeTab?: string;
  onChange?: (tab: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function Tabs({ defaultValue, activeTab: controlledTab, onChange, children, className }: TabsProps) {
  const [internalTab, setInternalTab] = useState(defaultValue);
  const isControlled = controlledTab !== undefined;
  const currentTab = isControlled ? controlledTab : internalTab;
  const setCurrentTab = isControlled
    ? (tab: string) => onChange?.(tab)
    : setInternalTab;

  return (
    <TabsContext.Provider value={{ activeTab: currentTab, setActiveTab: setCurrentTab }}>
      <div className={cn('tabs-container', className)}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function TabsList({ children, className }: TabsListProps) {
  return (
    <div className={cn('flex items-center gap-1 border-b border-border pb-px', className)}>
      {children}
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function TabsTrigger({ value, children, className }: TabsTriggerProps) {
  const { activeTab, setActiveTab } = useContext(TabsContext);
  const isActive = activeTab === value;

  return (
    <button
      onClick={() => setActiveTab(value)}
      className={cn(
        'px-3 py-2 text-sm transition-colors border-b-2',
        'focus:outline-none',
        isActive
          ? 'border-accent text-text-primary font-medium'
          : 'border-transparent text-text-muted hover:text-text-secondary',
        className
      )}
    >
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const { activeTab } = useContext(TabsContext);
  if (activeTab !== value) return null;

  return <div className={cn('mt-4', className)}>{children}</div>;
}
